/**
 * ChainSegmentStore — a tiny ring buffer of chained-projectile hops
 * (VFX-evolution S2.F2). ProjectileSystem records a from→to segment on each
 * chain bounce; the ability-capture sidecar reads the recent ones so the
 * verifier's `motion:'chain'` check has real segment data (it failed
 * explicitly before — sidecars carried no hops).
 *
 * Singleton + ring buffer, mirroring VisualLifecycleStore's shape. Pure
 * data; no Pixi/window. Cheap enough to always run (chain casts are rare).
 */
import type { ChainSegment } from '../../shared/diagnostics/CaptureSidecarTypes';

const MAX_SEGMENTS = 60;

class ChainSegmentStoreImpl {
  private buffer: ChainSegment[] = [];

  record(seg: ChainSegment): void {
    this.buffer.push(seg);
    if (this.buffer.length > MAX_SEGMENTS) this.buffer.shift();
  }

  /** All buffered segments (newest last). */
  all(): readonly ChainSegment[] {
    return this.buffer;
  }

  /** Segments at/after a timestamp floor — the cast-window slice. */
  since(tsFloor: number): ChainSegment[] {
    return this.buffer.filter((s) => s.ts >= tsFloor);
  }

  clear(): void {
    this.buffer = [];
  }
}

export const ChainSegmentStore = new ChainSegmentStoreImpl();
